import { ReactNode } from "react";
import styled, { css } from "styled-components";
import { Title, Body } from "sunday-component";
import type { SelectProps } from "@sunday/component";
import {
  FieldLabelText,
  FieldOptionalText,
} from "src/components/Common/ICTH/Typography";
import AutoComplete, {
  AutoCompleteProp,
  CustomDropdown,
} from "../AutoComplete";
import {
  AutoCompleteDropdownGlobalStyle,
  CustomSelect,
} from "../ICTH/Form/AutoCompleteForm";

const IceCreamSelect = styled(CustomSelect)`
  &&& {
    --select-input-border-radius: 8px;

    .ant-select-selector {
      border-color: var(--color-stroke-default, #d9dbde) !important;
      background: var(--color-surface-primary, #ffffff);
      box-shadow: none !important;
    }

    &:not(.ant-select-disabled):hover .ant-select-selector,
    &.ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
      border-color: var(--color-stroke-ci, #435dff) !important;
    }

    &.ant-select-disabled .ant-select-selector {
      border-color: var(--color-stroke-default, #d9dbde) !important;
      background: var(--color-surface-tertiary, #ecedef);
    }
  }
`;

export const AutoCompleteLabelText = styled(Title).attrs({ level: 7 })`
  color: var(--ui-form-input-select-activated-label_select-color);
`;
export const AutoCompleteErrorText = styled(Body).attrs({ level: 7 })`
  color: var(--ui-form-input-select-error-helper_select-color);
`;

export const AutoCompleteHelperText = styled(Body).attrs({ level: 7 })`
  color: var(--ui-form-input-select-activated-helper_select-color);
`;

export const AutoCompleteErrorCss = css`
  ${AutoCompleteLabelText} {
    color: var(--ui-form-input-select-error-label_select-color);
  }
  ${CustomDropdown}.sunday-component-select-show-search.sunday-component-select:not(.sunday-component-select-customize-input):not(.sunday-component-select-disabled) {
    .sunday-component-select-selector {
      background-color: var(--ui-form-input-select-error-background-color);
      border-color: var(--ui-form-input-select-error-border-color) !important;
    }

    .sunday-component-select-selection-placeholder {
      color: var(--ui-form-input-select-error-placeholder_select-color);
    }
    .sunday-component-select-arrow {
      color: var(--ui-form-input-select-error-icon-color);
    }
  }
`;

const Container = styled.div.attrs(
  (props: { disabled: boolean; isError: boolean }) => props
)`
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${IceCreamSelect} {
    font-size: 15px;
    font-family: "Noto Sans Thai", sans-serif;
  }

  ${(props) =>
    !props.disabled &&
    !props.isError &&
    `:hover {
    ${AutoCompleteLabelText} {
      color: var(--ui-form-input-select-hover-label_select-color);
    }
    ${AutoCompleteHelperText} {
      color: var(--ui-form-input-select-hover-helper_select-color);
    }
  }

  :focus-within {
    ${AutoCompleteLabelText} {
      color: var(--ui-form-input-select-focused-label_select-color);
    }
    ${AutoCompleteHelperText} {
      color: var(--ui-form-input-select-focused-helper_select-color);
    }
  }`}

  ${(props) => props.isError && AutoCompleteErrorCss}

  ${(props) =>
    props.disabled &&
    `
  ${AutoCompleteLabelText} {
    color: var(--ui-form-input-select-disabled-label_select-color) !important;
  }
  ${AutoCompleteHelperText} {
    color: var(--ui-form-input-select-disabled-helper_select-color);
  }
  `}
`;
export interface AutoCompleteFormProps extends AutoCompleteProp {
  label?: ReactNode;
  optionalText?: string;
  helperText?: string;
  errorText?: string;
  isError?: boolean;
  appearance?: "ice-cream";
}

const AutoCompleteForm = ({
  label,
  optionalText,
  placeholder,
  helperText,
  errorText,
  disabled = false,
  isError = false,
  appearance,
  dataTestId,
  dropdownClassName,
  ...props
}: AutoCompleteFormProps) => {
  const isIceCream = appearance === "ice-cream";

  return (
    <Container disabled={disabled} isError={isError}>
      <AutoCompleteLabelText as={isIceCream ? FieldLabelText : undefined}>
        {label}
        {optionalText && (
          <>
            {" "}
            <FieldOptionalText level={7}>{optionalText}</FieldOptionalText>
          </>
        )}
      </AutoCompleteLabelText>
      {isIceCream ? (
        <>
          <AutoCompleteDropdownGlobalStyle />
          <IceCreamSelect
            showSearch
            optionFilterProp="label"
            placeholder={placeholder}
            disabled={disabled}
            status={isError ? "error" : undefined}
            data-testid={dataTestId}
            data-gtm-input-code={dataTestId}
            popupClassName={dropdownClassName}
            {...(props as SelectProps)}
          />
        </>
      ) : (
        <AutoComplete
          dataTestId={dataTestId}
          placeholder={placeholder}
          disabled={disabled}
          dropdownClassName={dropdownClassName}
          {...props}
        />
      )}
      {isError && <AutoCompleteErrorText>{errorText}</AutoCompleteErrorText>}
      <AutoCompleteHelperText>{helperText}</AutoCompleteHelperText>
    </Container>
  );
};

export default AutoCompleteForm;
